// BP 56 §5 — the locale-preference store. Mirrors `themeStore.ts` exactly
// (zustand `persist`, applied via a side-effect on the DOM/i18next, imported
// for side-effect in `main.tsx` so first paint is correct) with one addition
// themeStore doesn't need: an `explicit` flag, because the locale precedence
// chain (§6) has to tell "the user (or the server) actually chose this" apart
// from "this is where navigator-language negotiation landed" — the same
// NULL-means-never-chosen distinction `users.locale` uses server-side
// (migration 147). `locale` itself stays a concrete, non-null `UiLocale` (like
// `theme`) so every render site has a value to show; `explicit` is the extra
// bit that answers "was this picked."
//
// `setLocale()` is the SINGLE write path: i18next switch (via `applyLocale`,
// instant once the target locale is cached) + localStorage (via `persist`) +
// `PATCH /api/v1/me/settings` when authenticated. The PATCH is fire-and-forget
// best-effort — the local UI has already switched by the time it lands, so a
// failed PATCH never blocks or reverts the visible change.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/apiClient';
import { useAuth } from '../stores/authStore';
import { applyLocale } from './runtime';
import { DEFAULT_LOCALE, isSupportedUiLocale, type UiLocale } from './constants';

const PERSIST_KEY = 'worksnip.locale';

interface LocaleState {
  /** Currently active UI locale — always concrete, never null. */
  locale: UiLocale;
  /** True once `locale` came from an explicit pick (switcher click, or a
   *  non-null server `user.locale` mirrored down at login) rather than from
   *  navigator negotiation defaulting it. */
  explicit: boolean;
  /** Switch the active locale. `opts.explicit` defaults to true (a real
   *  pick); the boot-time negotiation and the login-sync hook in
   *  `authStore.ts` pass `explicit:false`/`skipPatch:true` respectively for
   *  their own non-pick call sites (see negotiate.ts / authStore.ts). */
  setLocale: (code: UiLocale, opts?: { explicit?: boolean; skipPatch?: boolean }) => void;
}

export const useLocale = create<LocaleState>()(
  persist(
    (set) => ({
      locale: DEFAULT_LOCALE,
      explicit: false,
      setLocale: (code, opts) => {
        if (!isSupportedUiLocale(code)) return;
        void applyLocale(code); // fire-and-forget — instant once cached, react-i18next re-renders on completion
        set({ locale: code, explicit: opts?.explicit !== false });

        if (opts?.skipPatch) return;
        const { token } = useAuth.getState();
        if (!token) return; // anonymous — localStorage (via persist) is the only store
        api.patch('/api/v1/me/settings', { locale: code })
          .then((r) => {
            const returned = r?.data?.locale;
            // Partial update ONLY (never `setAuth(...)` — see authStore.ts's
            // module comment: a full setAuth resets activeOrgId and wipes
            // worksnip.projectTree.expanded / worksnip.chat).
            if (returned && returned !== useAuth.getState().user?.locale) {
              useAuth.setState({ user: { ...useAuth.getState().user, locale: returned } });
            }
          })
          .catch(() => { /* best-effort — the local switch already applied */ });
      },
    }),
    { name: PERSIST_KEY },
  ),
);

/** Reactive selector for the common case — `const locale = useActiveLocale();` */
export function useActiveLocale(): UiLocale {
  return useLocale((s) => s.locale);
}
